import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ModalComponent } from '../modal/modal.component';
import { ConfirmService } from './confirm.service';

/**
 * `<dros-confirm-dialog>` — the one styled confirmation prompt for the whole app,
 * mounted once at the app root and driven by {@link ConfirmService}. Every
 * destructive/confirm action routes through `ConfirmService.ask(...)` instead of
 * the browser's native `confirm()`, so they all look and behave identically
 * (matching the org-chart delete dialog).
 */
@Component({
  selector: 'dros-confirm-dialog',
  standalone: true,
  imports: [ModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (svc.state(); as s) {
      <dros-modal maxWidth="max-w-md" [hideHeader]="true" (closed)="svc.respond(false)">
        <div header class="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          @if ((s.tone ?? 'danger') === 'danger') {
            <i class="pi pi-exclamation-triangle text-rose-600 shrink-0"></i>
          }
          <div class="type-h2 text-slate-900">{{ s.title }}</div>
        </div>
        <div class="px-5 py-4 space-y-2">
          @if (s.message) {
            <p class="type-body text-slate-700 leading-relaxed whitespace-pre-line">{{ s.message }}</p>
          }
          @if (s.detail) {
            <p class="type-meta text-slate-500 leading-relaxed whitespace-pre-line">{{ s.detail }}</p>
          }
        </div>
        <div footer class="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 flex-wrap">
          @if (!s.acknowledgeOnly) {
            <button type="button" (click)="svc.respond(false)" class="type-button px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50">{{ s.cancelLabel || 'Cancel' }}</button>
          }
          <button
            type="button"
            (click)="svc.respond(true)"
            class="type-button px-4 py-1.5 text-white {{ (s.tone ?? 'danger') === 'danger' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-navy hover:bg-navy-deep' }}"
          >
            {{ s.confirmLabel || 'Confirm' }}
          </button>
        </div>
      </dros-modal>
    }
  `,
})
export class ConfirmDialogComponent {
  protected readonly svc = inject(ConfirmService);
}
